'use client';

import * as React from 'react';

import type { DropdownMenuProps } from '@radix-ui/react-dropdown-menu';
import type { TElement } from 'platejs';

import { ALargeSmall, Heading1Icon, Heading2Icon, Heading3Icon, type LucideIcon } from 'lucide-react';
import { KEYS } from 'platejs';
import { useEditorRef, useSelectionFragmentProp } from 'platejs/react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  getBlockType,
  setBlockType,
} from '@/components/editor/transforms';

import { ToolbarButton, ToolbarMenuGroup } from './toolbar';

const fieldTurnIntoItems: ReadonlyArray<{
  label: string;
  value: string;
  icon: LucideIcon;
}> = [
  {
    label: 'Paragraph',
    value: KEYS.p,
    icon: ALargeSmall,
  },
  {
    label: 'Heading 1',
    value: 'h1',
    icon: Heading1Icon,
  },
  {
    label: 'Heading 2',
    value: 'h2',
    icon: Heading2Icon,
  },
  {
    label: 'Heading 3',
    value: 'h3',
    icon: Heading3Icon,
  },
];

/** Turn-into menu scoped to field-tier block types only. */
export function FieldTurnIntoToolbarButton({
  disabled,
  ...props
}: DropdownMenuProps & { disabled?: boolean }) {
  const editor = useEditorRef();
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const value = useSelectionFragmentProp({
    defaultValue: KEYS.p,
    getProp: (node) => getBlockType(node as TElement),
  });
  const blockValue = value ?? KEYS.p;
  const menuValue = fieldTurnIntoItems.some((item) => item.value === blockValue)
    ? blockValue
    : KEYS.p;
  const selectedItem = React.useMemo(
    () =>
      fieldTurnIntoItems.find((item) => item.value === menuValue) ??
      fieldTurnIntoItems[0],
    [menuValue]
  );

  const SelectedIcon = selectedItem.icon;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false} {...props}>
      <DropdownMenuTrigger asChild>
        <ToolbarButton
          className="min-w-[6.5rem] text-xs"
          pressed={open}
          tooltip="Turn into"
          isDropdown
          disabled={disabled}
        >
          <SelectedIcon className="size-4 shrink-0" aria-hidden />
          {selectedItem.label}
        </ToolbarButton>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className="ignore-click-outside/toolbar min-w-0"
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          editor.tf.focus();
        }}
        align="start"
      >
        <ToolbarMenuGroup
          value={menuValue}
          onValueChange={(type) => {
            setBlockType(editor, type);
          }}
          label="Turn into"
        >
          {fieldTurnIntoItems.map(({ icon: Icon, label, value: itemValue }) => (
            <DropdownMenuRadioItem
              key={itemValue}
              className="min-w-[10rem]"
              value={itemValue}
            >
              <Icon className="size-4 shrink-0" aria-hidden />
              {label}
            </DropdownMenuRadioItem>
          ))}
        </ToolbarMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
